import React, { useState, useMemo, useEffect } from "react";
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Collapse,
  IconButton,
  Box,
  Typography,
  MenuItem,
  Select,
} from "@mui/material";
import { Delete, Edit, Save, Cancel, ExpandLess, ExpandMore } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import moment from "moment";
import DataTableHeadCell from "./DataTableHeadCell";
import { formatMonthYearToInput } from "utils/CalanderUtils";
import { NumericFormat } from "react-number-format";

interface CollapsiblePivotTableProps {
  columns: any[];
  data: any[];
  onDelete: (id: any) => void;
  onEdit: (row: any | null) => void;
  onSave: (row: any) => void;
  onCancel: () => void;
  editingRow: any | null;
  isEditing: boolean;
  onInputChange: (value: any, field: string) => void;
  selectedRows: any[];
  onSelectRow: (rowKey: any) => void;
  onSelectAll: () => void;
  onSort: (columnKey: any) => void;
  sortedColumn?: string | null;
  sortDirection?: "asc" | "desc";
  getRowStyle: (row: any) => any;
  isActionsDisabled?: (row: any) => boolean;
  rowKey: string;
  renderTotal?: (rows: any[], key: string) => React.ReactNode;
  totalColumn?: string[] | null;
  groupBy?: string;
  groupFormatter?: (value: any) => string;
  defaultExpanded?: boolean;
}

const CollapsiblePivotTable: React.FC<CollapsiblePivotTableProps> = ({
  columns,
  data,
  onDelete,
  onEdit,
  onSave,
  onCancel,
  editingRow,
  isEditing,
  onInputChange,
  selectedRows,
  onSelectRow,
  onSelectAll,
  onSort,
  sortedColumn,
  sortDirection,
  getRowStyle,
  isActionsDisabled,
  rowKey,
  renderTotal,
  totalColumn,
  groupBy,
  groupFormatter,
  defaultExpanded,
}) => {
  const sortedData = useMemo(() => {
    if (!sortedColumn) return data;

    const sorted = [...data].sort((a, b) => {
      const aValue = a[sortedColumn];
      const bValue = b[sortedColumn];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
      }

      return sortDirection === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    return sorted;
  }, [data, sortedColumn, sortDirection]);

  const groupedData = useMemo(() => {
    const groups: Record<string, any[]> = {};
    sortedData.forEach((row) => {
      const groupValue = row[groupBy];
      const groupKey = groupFormatter(groupValue);

      if (!groups[groupKey]) groups[groupKey] = [];
      groups[groupKey].push(row);
    });

    return groups;
  }, [sortedData, groupBy, groupFormatter]);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    const initialState: Record<string, boolean> = {};
    Object.keys(groupedData).forEach((key) => {
      initialState[key] = defaultExpanded;
    });
    return initialState;
  });

  useEffect(() => {
    setExpandedGroups((prev) => {
      const newState = { ...prev };
      Object.keys(groupedData).forEach((key) => {
        if (newState[key] === undefined) {
          newState[key] = defaultExpanded;
        }
      });
      return newState;
    });
  }, [groupedData, defaultExpanded]);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName],
    }));
  };

  const allRowKeys = data.map((row) => row[rowKey]);
  const allSelected =
    allRowKeys.length > 0 && allRowKeys.every((key) => selectedRows.includes(key));

  const handleSelectAllChange = () => {
    onSelectAll();
  };

  const handleCheckboxChange = (key: any) => {
    onSelectRow(key);
  };

  return (
    <TableContainer sx={{ boxShadow: "none", width: "100%" }}>
      <Table style={{ width: "100%", tableLayout: "fixed" }}>
        <MDBox component="thead">
          <TableRow>
            <TableCell
              align="center"
              padding="checkbox"
              sx={{
                padding: "8px",
                width: "40px",
                minWidth: "40px",
                maxWidth: "40px",
              }}
            >
              <Checkbox
                checked={allSelected}
                onChange={handleSelectAllChange}
                color="primary"
                size="small"
              />
            </TableCell>
            {columns.map((col) => (
              <DataTableHeadCell
                key={col.accessor}
                align={col.align || "left"}
                width={col.width}
                sorted={
                  sortedColumn === col.accessor
                    ? sortDirection === "asc"
                      ? "asce"
                      : "desc"
                    : "none"
                }
                onClick={() => onSort(col.accessor)}
              >
                {col.Header}
              </DataTableHeadCell>
            ))}
            {(onDelete || onEdit)  && <DataTableHeadCell width="120px">Actions</DataTableHeadCell>}
          </TableRow>
        </MDBox>

        <TableBody>
          {Object.entries(groupedData).map(([groupName, rows]) => {
            const isExpanded = expandedGroups[groupName] ?? defaultExpanded;

            return (
              <React.Fragment key={groupName}>
                <TableRow hover>
                  <TableCell
                    colSpan={columns.length + 2}
                    sx={{ padding: "1px", borderBottom: "none", paddingLeft: "10px" }}
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      onClick={() => toggleGroup(groupName)}
                      sx={{
                        cursor: "pointer",
                        padding: "8px",
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.08)",
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleGroup(groupName);
                        }}
                      >
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                      <Typography sx={{ ml: 1, fontWeight: "bold", fontSize: "15px" }}>
                        {groupName} ({rows.length} item{rows.length !== 1 ? "s" : ""})
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    colSpan={columns.length + 2}
                    sx={{ padding: "0", borderBottom: "none" }}
                  >
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ margin: "8px 0", paddingLeft: "20px" }}>
                        <Table
                          size="small"
                          aria-label={`rows-for-${groupName}`}
                          style={{ tableLayout: "fixed" }}
                        >
                          <TableBody>
                            {rows.map((row) => {
                              const isRowEditing =
                                editingRow && editingRow[rowKey] === row[rowKey] && isEditing;

                              if (isRowEditing) {
                                return (
                                  <TableRow key={row[rowKey]}>
                                    <TableCell
                                      padding="checkbox"
                                      sx={{ width: "40px" }}
                                    ></TableCell>

                                    {columns.map((col) => {
                                      const cellValue = editingRow[col.accessor] || "";

                                      return (
                                        <TableCell
                                          key={col.accessor}
                                          sx={{
                                            padding: "8px",
                                            width: col.width || "auto",
                                            minWidth: col.width || "auto",
                                            maxWidth: col.width || "auto",
                                          }}
                                        >
                                          {col.input === "select" ? (
                                            <Select
                                              variant="standard"
                                              endAdornment={<ExpandMore />}
                                              value={
                                                col.selector
                                                  ? editingRow[col.selector]
                                                  : editingRow[col.accessor] || ""
                                              }
                                              onChange={(e) => {
                                                if (col.selector) {
                                                  onInputChange(e.target.value, col.selector);
                                                  onInputChange(e.target.value, col.accessor);
                                                } else {
                                                  onInputChange(e.target.value, col.accessor);
                                                }
                                              }}
                                              sx={{
                                                width: col.width,
                                                maxWidth: col.width,
                                                fontSize: "0.75rem",
                                                fontWeight: 700,
                                              }}
                                            >
                                              {col.options?.map((option: any, idx: number) => (
                                                <MenuItem key={idx} value={option.value}>
                                                  {option.label}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                          ) : col.input === "checkbox" ? (
                                            <Checkbox
                                              checked={!!cellValue}
                                              onChange={(e) =>
                                                onInputChange(e.target.checked, col.accessor)
                                              }
                                            />
                                          ) : col.input === "amount" ? (
                                            <NumericFormat
                                              variant="standard"
                                              value={cellValue}
                                              thousandSeparator
                                              prefix="$"
                                              decimalScale={2}
                                              fixedDecimalScale
                                              allowNegative={false}
                                              customInput={MDInput}
                                              onValueChange={({ value }) => {
                                                const formatted = parseFloat(
                                                  value.replace(/[^0-9.-]+/g, "")
                                                );
                                                onInputChange(formatted, col.accessor);
                                              }}
                                              sx={{
                                                width: "100%",
                                                fontSize: "0.75rem",
                                                fontWeight: 700,
                                              }}
                                            />
                                          ) : col.input === "monthYear" ? (
                                            <MDInput
                                              variant="standard"
                                              type="month"
                                              value={formatMonthYearToInput(
                                                editingRow[col.accessor]
                                              )}
                                              onChange={(e: any) => {
                                                const value = e.target.value;
                                                if (!value) {
                                                  onInputChange("", col.accessor);
                                                  return;
                                                }
                                                const dt = moment(value, "YYYY-MM", true);
                                                if (dt.isValid()) {
                                                  const formatted = dt.format("MMMM YYYY");
                                                  onInputChange(formatted, col.accessor);
                                                } else {
                                                  onInputChange("", col.accessor);
                                                }
                                              }}
                                              sx={{
                                                fontSize: "0.75rem",
                                                fontWeight: 700,
                                                width: "100%",
                                              }}
                                            />
                                          ) : col.input === "date" ? (
                                            <MDInput
                                              variant="standard"
                                              type="date"
                                              value={cellValue}
                                              onChange={(e: any) =>
                                                onInputChange(e.target.value, col.accessor)
                                              }
                                              sx={{ fontSize: "0.75rem", fontWeight: 700 }}
                                            />
                                          ) : col.input === "number" ? (
                                            <MDInput
                                              variant="standard"
                                              type="number"
                                              value={cellValue}
                                              onChange={(e: any) =>
                                                onInputChange(e.target.value, col.accessor)
                                              }
                                              sx={{ fontSize: "0.75rem", fontWeight: 700 }}
                                            />
                                          ) : (
                                            <MDInput
                                              variant="standard"
                                              value={cellValue}
                                              onChange={(e: any) =>
                                                onInputChange(e.target.value, col.accessor)
                                              }
                                              sx={{ fontSize: "0.75rem", fontWeight: 700 }}
                                            />
                                          )}
                                        </TableCell>
                                      );
                                    })}

                                    <TableCell sx={{ width: "120px", padding: "8px" }}>
                                      <MDBox display="flex" gap={0.5}>
                                        <MDButton
                                          variant="text"
                                          color="primary"
                                          onClick={() => onSave(editingRow)}
                                          startIcon={<Save />}
                                          sx={{ minWidth: "auto", padding: "4px 8px" }}
                                        ></MDButton>
                                        <MDButton
                                          variant="text"
                                          color="secondary"
                                          onClick={onCancel}
                                          startIcon={<Cancel />}
                                          sx={{ minWidth: "auto", padding: "4px 8px" }}
                                        ></MDButton>
                                      </MDBox>
                                    </TableCell>
                                  </TableRow>
                                );
                              }

                              return (
                                <TableRow key={row[rowKey]}>
                                  <TableCell
                                    padding="checkbox"
                                    align="center"
                                    sx={{
                                      padding: "8px",
                                      width: "40px",
                                      minWidth: "40px",
                                      maxWidth: "40px",
                                    }}
                                  >
                                    <Checkbox
                                      checked={selectedRows.includes(row[rowKey])}
                                      onChange={() => handleCheckboxChange(row[rowKey])}
                                      color="primary"
                                      size="small"
                                    />
                                  </TableCell>
                                  {columns.map((col) => (
                                    <TableCell
                                      key={col.accessor}
                                      sx={{
                                        padding: "8px",
                                        width: col.width || "auto",
                                        minWidth: col.width || "auto",
                                        maxWidth: col.width || "auto",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                      }}
                                    >
                                      <MDTypography
                                        fontSize="0.75rem"
                                        fontWeight={getRowStyle(row)}
                                        noWrap
                                      >
                                        {col.Cell ? col.Cell({ row }) : row[col.accessor]}
                                      </MDTypography>
                                    </TableCell>
                                  ))}
                                 
                                 { (onDelete || onEdit) &&
                                  <TableCell
                                    align="center"
                                    sx={{
                                      padding: "8px",
                                      width: "120px",
                                      minWidth: "120px",
                                      maxWidth: "120px",
                                    }}
                                  >
                                    <MDBox display="flex" gap={0.5}>
                                   
                                    {onDelete &&
                                       <MDButton
                                        variant="text"
                                        color="error"
                                        onClick={() => onDelete(row[rowKey])}
                                        startIcon={<Delete />}
                                        sx={{ minWidth: "auto", padding: "4px 8px" }}
                                        disabled={
                                          isActionsDisabled ? isActionsDisabled(row) : false
                                        }
                                      ></MDButton>}
                                       

                                      {onEdit &&
                                      <MDButton
                                        variant="text"
                                        color="secondary"
                                        onClick={() => onEdit(row)}
                                        startIcon={<Edit />}
                                        sx={{ minWidth: "auto", padding: "4px 8px" }}
                                        disabled={
                                          isActionsDisabled ? isActionsDisabled(row) : false
                                        }
                                      ></MDButton>
                                      }
                                    </MDBox>
                                  </TableCell>
                               }
                                </TableRow>
                              );
                            })}

                            {renderTotal && (
                              <TableRow key="totals">
                                <TableCell></TableCell>
                                <TableCell align="left">
                                  <MDTypography fontSize={"0.85rem"} fontWeight={"bold"}>
                                    TOTAL:
                                  </MDTypography>
                                </TableCell>

                                {columns.map((column, index) => {
                                  if (index < 1) return;
                                  if (totalColumn?.includes(column.accessor)) {
                                    return (
                                      <TableCell
                                        key={column.accessor}
                                        sx={{ padding: "8px" }}
                                        align="left"
                                      >
                                        <MDTypography fontSize={"0.85rem"} fontWeight={"bold"}>
                                          {renderTotal(rows, column.accessor)}
                                        </MDTypography>
                                      </TableCell>
                                    );
                                  } else {
                                    return (
                                      <TableCell key={column.accessor} sx={{ padding: "8px" }}>
                                        <MDTypography
                                          fontSize={"0.75rem"}
                                          fontWeight={700}
                                        ></MDTypography>
                                      </TableCell>
                                    );
                                  }
                                })}
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CollapsiblePivotTable;
